import { useEffect, useMemo, useState } from 'react';
import { fetchAllProducts } from '../api/products'; // new/all-products endpoint
import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import SearchBar from '../components/SearchBar';

const itemsPerPage = 20;

const Shop = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await fetchAllProducts();
        // if backend returns array:
        // setAllProducts((data || []).filter(p => p.is_active));
        // if backend returns { products: [...] }:
        const products = (data || []).filter(p => p.is_active).sort((a, b) => (b.images.length > 0 || b.image_url != null) - (a.images.length > 0 || a.image_url != null)).filter((p) => p.stock_count > 0);
        setAllProducts(products);
      } catch (err) {
        console.error('Error loading products:', err);
      }
    };
    loadProducts();
  }, []);

  // all categories from ALL products
  const allCategories = useMemo(
    () => [...new Set(allProducts.map(p => p.product_group_name).filter(Boolean))],
    [allProducts]
  );

  const handleToggleTag = (tag) => {
    const next = new Set(selectedCategories);
    next.has(tag) ? next.delete(tag) : next.add(tag);
    setSelectedCategories(next);
  };

  const applyFilters = () => {
    setPage(1);
    setIsSidebarOpen(false);
  };

  // category filter
  const filtered = useMemo(() => {
    if (selectedCategories.size === 0) return allProducts;
    return allProducts.filter(p => selectedCategories.has(p.product_group_name));
  }, [allProducts, selectedCategories]);

  // search on top of category filter
  const searched = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return filtered;

    return filtered.filter(
      (p) =>
        p.product_name?.toLowerCase().includes(q) ||
        p.subname?.toLowerCase().includes(q)
    );
  }, [filtered, searchQuery]);

  const showingAll = searchQuery.trim().length > 0;

  const totalPages = useMemo(() => {
    if (showingAll) return 1; // pagination hidden in this case anyway
    return Math.max(1, Math.ceil(searched.length / itemsPerPage));
  }, [searched.length, showingAll]);

  const paginated = useMemo(() => {
    if (showingAll) return searched; // during search show all matches
    const start = (page - 1) * itemsPerPage;
    return searched.slice(start, start + itemsPerPage);
  }, [searched, page, showingAll]);

  const listToRender = paginated;

  const shouldShowPagination = !showingAll && totalPages > 1;

  return (
    <div className="flex pb-20">
      {/* Sidebar */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black/30" onClick={applyFilters}>
          <div
            className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-md p-4 z-30 overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold mb-4">Filtriraj po Kategoriji</h2>
            {allCategories.map((cat) => (
              <label key={cat} className="block mb-2">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selectedCategories.has(cat)}
                  onChange={() => handleToggleTag(cat)}
                />
                {cat}
              </label>
            ))}
            <button
              onClick={applyFilters}
              className="mt-4 px-4 py-2 bg-black text-white rounded"
            >
              Primeni filter
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="w-full p-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h1 className="text-3xl font-bold text-center sm:text-left">Prodavnica</h1>
          <div className="flex items-center gap-3">
            <SearchBar
              value={searchQuery}
              onChange={(v) => {
                setSearchQuery(v);
                setPage(1);
                if (!v) window.scrollTo({ top: 0 });
              }}
            />
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="px-4 py-2 border rounded"
            >
              Kategorije
            </button>
          </div>
        </div>

        {/* Info row */}
        {showingAll ? (
          <div className="text-sm text-gray-600 mb-3">
            Prikazano <b>{searched.length}</b> rezultata za “{searchQuery}”
          </div>
        ) : (
          <div className="text-sm text-gray-600 mb-3">
            Stranica <b>{page}</b> od <b>{totalPages}</b> — {searched.length} proizvod
            {searched.length !== 1 ? 'a' : ''}
          </div>
        )}

        {/* Top Pagination */}
        {shouldShowPagination && (
          <Pagination
            current={page}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="mb-6"
          />
        )}

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {listToRender.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Bottom Pagination */}
        {shouldShowPagination && (
          <Pagination
            current={page}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="mt-6"
          />
        )}
      </div>
    </div>
  );
};

export default Shop;
